import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';
import { IDropdownItem } from '../Dropdown/models/IDropdownItem.js';
import Image from '../Image/Image.js';

import { IDateDropdownConfiguration, IDateDropdownProps, IDateDropdownState } from './models/index.js';
import ClickLayer from './styledComponents/ClickLayer.js';
import Header from './styledComponents/Header.js';
import HeaderDisabled from './styledComponents/HeaderDisabled.js';
import HeaderError from './styledComponents/HeaderError.js';
import HeaderIcon from './styledComponents/HeaderIcon.js';
import HeaderTitle from './styledComponents/HeaderTitle.js';
import List from './styledComponents/List.js';
import ListItem from './styledComponents/ListItem.js';
import ListItemLabel from './styledComponents/ListItemLabel.js';
import ListItems from './styledComponents/ListItems.js';
import Placeholder from './styledComponents/Placeholder.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DATE_DROPDOWN_CATEGORY_KEY, DATE_DROPDOWN_COMPONENT_KEY } from './DateDropdown.meta.js';

export class DateDropdown extends ComponentBase<IDateDropdownProps, IDateDropdownState, IDateDropdownConfiguration> {
	categoryKey = DATE_DROPDOWN_CATEGORY_KEY;
	componentKey = DATE_DROPDOWN_COMPONENT_KEY;

	constructor(props: IDateDropdownProps) {
		super(props);

		makeObservable(this, {
			isOpen: observable,
			selectedDate: observable.ref,
			toggleOpen: action,
			handleOnChange: action,
			selectedDateDisplay: computed,
			items: computed,
			setSelectedDate: action,
		});

		const { selectedDate, dates = [] } = this.props;
		if (selectedDate && dates.includes(selectedDate)) {
			this.selectedDate = selectedDate;
		}
	}

	isOpen = false;
	selectedDate: string | undefined = undefined;

	formatDate = (date: string) => {
		const { formatOptions, locale = 'en-GB' } = this.props;
		if (formatOptions) {
			return common.commonUtils.formatDateTime(locale, formatOptions, date);
		}
		return date;
	};

	toggleOpen = () => {
		this.isOpen = !this.isOpen;
	};

	handleOnChange = (
		e: React.MouseEvent<HTMLLIElement, MouseEvent> | React.ChangeEvent<HTMLInputElement>,
		value: string,
	) => {
		e.preventDefault();
		const { onChange } = this.props;
		this.selectedDate = value;
		this.toggleOpen();
		if (onChange) {
			onChange(value);
		}
	};

	get selectedDateDisplay() {
		if (this.selectedDate) {
			return this.formatDate(this.selectedDate);
		}

		const { placeholder = '' } = this.props;
		return <Placeholder>{placeholder}</Placeholder>;
	}

	get items(): IDropdownItem[] {
		const { dates = [], placeholder = '' } = this.props;
		const placeholderItem = {
			value: '',
			label: placeholder,
		};

		const items = dates.map((date: string) => {
			return {
				value: date,
				label: this.formatDate(date),
			};
		});
		return [placeholderItem, ...(items as any)];
	}

	componentWillReceiveProps(props: IDateDropdownProps) {
		// this logic allows a parent component to manage the selected values, opposed to the default internal state management
		const nextSelected = props.selectedDate;
		const currSelected = this.props.selectedDate;
		if (currSelected !== nextSelected) {
			this.setSelectedDate(nextSelected);
		}
	}

	setSelectedDate = (selectedDate: string | undefined) => {
		this.selectedDate = selectedDate;
	};

	render() {
		const { className, hasError = false, disabled = false } = this.props;
		const usePlaceholder = common.commonUtils.isNullOrEmpty(this.selectedDate);
		const asset = `base.dateDropdown.${usePlaceholder ? 'calendarPlaceholder' : 'calendar'}`;
		// eslint-disable-next-line @typescript-eslint/naming-convention
		const HeaderComponent = disabled ? HeaderDisabled : hasError ? HeaderError : Header;

		return (
			<Wrapper className={className}>
				<HeaderComponent onClick={() => !disabled && this.toggleOpen()}>
					<HeaderIcon>
						<Image asset={asset} />
					</HeaderIcon>
					<HeaderTitle>{this.selectedDateDisplay}</HeaderTitle>
				</HeaderComponent>
				{this.isOpen && (
					<Fragment>
						<ClickLayer onClick={this.toggleOpen} />
						<List>
							<ListItems>
								{this.items.map((item: IDropdownItem) => {
									return (
										<ListItem
											key={item.value}
											onClick={(e: any) => this.handleOnChange(e, item.value)}
										>
											<ListItemLabel>{item.label}</ListItemLabel>
										</ListItem>
									);
								})}
							</ListItems>
						</List>
					</Fragment>
				)}
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(DateDropdown), DATE_DROPDOWN_CATEGORY_KEY, DATE_DROPDOWN_COMPONENT_KEY);
